import { useNavigate, useParams } from "react-router-dom";
import { useGetDatasetProtocolDatasetProtocolDatasetHashGet, useGetDatasetsDatasetGet } from "../../../api/generated/fastAPI";
import { useEffect, useState, MouseEvent, ChangeEvent, useMemo } from "react";
import { Dataset, Protocol, StageChanges } from "../../../api/models";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import NewPageTabs from "../../../components/Tabs/NewPageTabs";
import SecondaryTabs from "../../../components/Tabs/SecondaryTabs";
import { TableDataField, TableHeader } from "../../../components/Table/DataTable";
import ProtocolTable, { NewProtocolTableData } from "../components/ProtocolTable";
import { ReactComponent as RecordsIcon } from '../../../assets/Icons/adornments/Records.svg';
import { capitalize } from "../../../utils/formattingFns";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import CheckboxButton from "../../../components/Buttons/CheckboxButton";
import useUpdate from "../../../hooks/endpoints/useUpdate";
import useMessage from "../../../hooks/useMessage";
import SnackBar from "../../../components/Alerts/SnackBar";
import { formatDisplayedDatasetMetadata, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import useDownload from "../../../hooks/endpoints/useDownload";
import useColorPalette from "../../../hooks/useColorPalette";

const tabs = [ 'Data', 'Protocol' ];

export default function DatasetProtocol() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { palette } = useColorPalette(); 

    const datasetMutation = useGetDatasetsDatasetGet({ dataset_hash: id ? id : '' });
    const [ dataset, setDataset ] = useState<Dataset | null>(null);
    const [ datasetMetadata, setDatasetMetadata ] = useState<MetadataMenuItemType[]>([]);
    const { downloadDatasetProtocol } = useDownload();

    const protocolMutation = useGetDatasetProtocolDatasetProtocolDatasetHashGet(id ? id : '');
    const [ protocol, setProtocol ] = useState<Protocol | null>(null);

    const [ originalData, setOriginalData ] = useState<NewProtocolTableData[]>([]);
    const [ transformedData, setTransformedData ] = useState<NewProtocolTableData[]>([]);

    const [ originalHeaders, setOriginalHeaders ] = useState<TableHeader[]>([]);
    const [ transformedHeaders, setTransformedHeaders ] = useState<TableHeader[]>([]);

    const [ secondaryTabs, setSecondaryTabs ] = useState<{label: string, count: number}[]>([]);
    const [ selectedIndex, setSelectedIndex ] = useState(1);
    const [ selectedSecondaryIndex, setSelectedSecondaryIndex ] = useState(0);
    const [ isOriginalShown, setIsOriginalShown ] = useState(false);

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    // const [ classOriginData, setClassOriginData ] = useState<{ stats?: ColumnMetadataOutputStats, classOrigin?: ColumnMetadataOutputClassOrigin } | null>(null);
    const { updateDataset } = useUpdate({ 
        setPopupMessage, 
        refetchFn: () => datasetMutation.refetch(), 
        itemToUpdate: dataset 
    })

    const handleTabChange = (event: React.SyntheticEvent, index: number) => {
        setSelectedIndex(index);
        if (index === 0) {
            navigate(`/datasets/${id}`);
        }
    }

    useEffect(() => {
        if (datasetMutation.data) {
            const data = datasetMutation.data as unknown as Dataset;
            setDataset(data);

            // const columnMetadata = data.dataset_metadata!.column_metadata;

            // if (columnMetadata) {
            //     const classOriginKey = Object.keys(columnMetadata).find(column => column === 'continuous-value' || column === 'single-class-label');
            //     if (classOriginKey) {
            //         if (columnMetadata[classOriginKey]) {
            //             const classOriginData = columnMetadata[classOriginKey];

            //             setClassOriginData({
            //                 stats: classOriginData!.stats,
            //                 classOrigin: classOriginData!.class_origin,
            //             });

            //         } else {
            //             setClassOriginData(null);
            //         }
                    
            //     }
            // }
        }
    }, [datasetMutation.data]);

    useEffect(() => {
        const fetchDsMetadata = async (ds: Dataset) => {
            const metadata = await formatDisplayedDatasetMetadata(ds);
            setDatasetMetadata(metadata);
        }

        if (dataset) {
            fetchDsMetadata(dataset);
        }
    }, [dataset]);

    useEffect(() => {
        if (protocolMutation.data) {
            const data = protocolMutation.data as unknown as Protocol;
            setProtocol(data);

            const functionNames: {label: string, count: number}[] = [];

            const stages = data.protocol_metadata!.stage_changes!;
            stages.forEach(stage => {
                functionNames.push({
                    label: stage.function!,
                    count: stage.standardization_records ? stage.standardization_records.length : 0,
                })
            });

            setSecondaryTabs(functionNames);

        }
    }, [protocolMutation.data]);

    useEffect(() => {
        if (protocol && secondaryTabs.length > 0) {
            const stages = protocol.protocol_metadata!.stage_changes!;
            const selectedTab = secondaryTabs[selectedSecondaryIndex];

            let selectedStage: StageChanges | undefined;

            stages.forEach(stage => {
                if (stage.function! === selectedTab.label) {
                    selectedStage = stage;
                }
            });

            if (selectedStage) {
                const records = selectedStage.standardization_records;

                if (records && records.length > 0) {
                    records.sort((a,b) => {
                        if (a.ordinal! < b.ordinal!) {
                            return -1;
                        } else if (a.ordinal! > b.ordinal!) {
                            return 1;
                        } else {
                            return 0;
                        }
                    });

                    const originalFieldsData: NewProtocolTableData[] = [];
                    const transformedFieldsData: NewProtocolTableData[] = [];

                    records.forEach(record => {
                        const originalTableFields: TableDataField[] = [];
                        const originalFields = record.original_fields;

                        const transformedTableFields: TableDataField[] = [];
                        const transformedFields = record.transformed_fields;
                        if (originalFields) {
                            const smilesKey = Object.keys(originalFields).find(key => key.toLocaleLowerCase().includes('smiles'));
                            if (smilesKey) {
                                originalTableFields.push({
                                    associatedColumn: smilesKey,
                                    type: 'structure',
                                    alignment: 'left',
                                    value: originalFields[smilesKey] as string,
                                })
                            }
                            for (const [key, value] of Object.entries(originalFields)) {
                                originalTableFields.push({
                                    associatedColumn: key,
                                    type: 'text',
                                    alignment: 'center',
                                    value: value !== null ? typeof value === 'string' ? value.trim() : (value as any).toString() : '',
                                })
                            }
                        }
                        originalFieldsData.push({
                            id: record.ordinal!,
                            fields: originalTableFields,
                            result: record.result!,
                        })

                        if (transformedFields) {
                            const smilesKey = Object.keys(transformedFields).find(key => key.toLocaleLowerCase().includes('smiles'));
                            if (smilesKey) {
                                transformedTableFields.push({
                                    associatedColumn: smilesKey,
                                    type: 'structure',
                                    alignment: 'left',
                                    value: transformedFields[smilesKey] as string,
                                })
                            }
                            for (const [key, value] of Object.entries(transformedFields)) {
                                transformedTableFields.push({
                                    associatedColumn: key,
                                    type: 'text',
                                    alignment: 'center',
                                    value: value !== null ? typeof value === 'string' ? value.trim() : (value as any).toString() : '',
                                })
                            }
                        }

                        transformedFieldsData.push({
                            id: record.ordinal!,
                            fields: transformedTableFields,
                            result: record.result!,
                        })
                        
                    });

                    let cleanedTransformedFieldsData: NewProtocolTableData[] = []; 

                    const ogHeaders: TableHeader[] = [];
                    const trHeaders: TableHeader[] = [];

                    if (originalFieldsData.length > 0) {
                        if (transformedFieldsData.length > 0) {
                            originalFieldsData.forEach(fieldData => {
                                const matchingTransformedFieldsData = transformedFieldsData.find(data => data.id === fieldData.id);
                                if (matchingTransformedFieldsData) {
                                    let newTransformedFields: TableDataField[] = [];

                                    let ogFields = fieldData.fields;
                                    let trFields = matchingTransformedFieldsData.fields;

                                    if (fieldData.result === 'removed') {
                                        newTransformedFields = ogFields;
                                    } else {
                                        if (ogFields.find(field => field.associatedColumn === 'SMILES_datafile_structure')) {
                                            trFields = matchingTransformedFieldsData.fields.map(field => {
                                                if (field.associatedColumn === 'SMILES_dataset_structure') {
                                                    return {
                                                        ...field,
                                                        associatedColumn: 'SMILES_datafile_structure'
                                                    }
                                                } else {
                                                    return {...field}
                                                }
                                            })
                                        }

                                        ogFields.forEach(field => {
                                            if (!trFields.find(trField => trField.associatedColumn === field.associatedColumn)) {
                                                trFields.push({...field})
                                            } 
                                        });

                                        newTransformedFields = trFields;
                                    }

                                    cleanedTransformedFieldsData.push({
                                        ...fieldData,
                                        fields: newTransformedFields,
                                    })

                                    
                                }
                            })
                        } else {
                            cleanedTransformedFieldsData = originalFieldsData;
                        }

                        originalFieldsData[0].fields.forEach((field, i) => {
                            ogHeaders.push({
                                label: field.type === 'structure' ? 'Chemical Structure' : field.associatedColumn === 'SMILES_datafile_structure' ? 'SMILES' : field.associatedColumn.split('_').map(word => capitalize(word)).join(' '),
                                value: field.associatedColumn,
                                isShown: true,
                                alignment: i === 0 ? 'left' : 'center',
                            })
                        });
                    };

                    if (cleanedTransformedFieldsData.length > 0) {
                        cleanedTransformedFieldsData[0].fields.forEach((field, i) => {
                            trHeaders.push({
                                label: field.type === 'structure' ? 'Chemical Structure' : field.associatedColumn === 'SMILES_datafile_structure' ? 'SMILES' : field.associatedColumn.split('_').map(word => capitalize(word)).join(' '),
                                value: field.associatedColumn,
                                isShown: true,
                                alignment: i === 0 ? 'left' : 'center',
                            })
                        })
                    };
                    
                    setOriginalData(originalFieldsData);
                    setTransformedData(cleanedTransformedFieldsData);
                    setOriginalHeaders(ogHeaders);
                    setTransformedHeaders(trHeaders);

                } else {
                    setOriginalData([]);
                    setTransformedData([]);
                    setOriginalHeaders([]);
                    setTransformedHeaders([]);
                };
            }
        }
    }, [protocol, secondaryTabs, selectedSecondaryIndex]);

    const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
        setIsOriginalShown(e.target.checked);
        setTransformedHeaders(transformedHeaders.map(header => ({...header, isShown: true})));
        setOriginalHeaders(originalHeaders.map(header => ({...header, isShown: true})))

    }

    const dropdownOptions: DropdownOption[] = useMemo(() => {
        const options: DropdownOption[] = [];

        if (dataset && protocol) {
            options.push({
                label: 'Export',
                action: async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    try {
                        await downloadDatasetProtocol(dataset, protocol);
                    } catch (err:any) {
                        console.log(err);
                    }
                }
            })
        }

        return options;

    }, [dataset, protocol, downloadDatasetProtocol]);

    console.log(protocol)


    return (
        <div className={`h-screen overflow-hidden flex flex-col justify-start items-stretch gap-4 p-10`}>
            <SinglePageHeader 
                label='Protocol'
                isLoading={datasetMutation.isLoading || protocolMutation.isLoading} 
                pages={[
                    {
                        label: 'Datasets',
                        action: (e: MouseEvent<HTMLButtonElement>) => { navigate('/datasets') }
                    },
                    {
                        label: dataset ? dataset.name! : '',
                        action: (e: MouseEvent<HTMLButtonElement>) => { navigate(`/datasets/${dataset ? dataset.file_hash! : ''}`) }
                    },
                    {
                        label: 'Protocol',
                    }
                ]}
                itemMetadata={[{
                    label: 'Records',
                    value: protocol && protocol.protocol_metadata ? protocol.protocol_metadata.total_changes!.records_number!.toString() : '0',
                    adornment: <RecordsIcon/>
                }]}
            >
                <NestedDropdown
                    dir="right"
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'single-ds-options'
                    }}
                    options={dropdownOptions}
                />
                {datasetMetadata.length > 0 && (
                    <UpdatedMetadataMenu 
                        menuLabel="Dataset Metadata"
                        menuItems={datasetMetadata}
                        updateFunc={updateDataset}
                        location="page"
                    />
                )}
            </SinglePageHeader>

            <div>
                <NewPageTabs 
                    tabs={tabs.map((tab,i) => ({
                        label: tab,
                        index: i
                    }))}
                    selectedIndex={selectedIndex}
                    handleTabChange={handleTabChange}
                />
            </div>
            
            {secondaryTabs.length > 0 && (
                <div>
                    <SecondaryTabs 
                        tabs={secondaryTabs.map((tab,i) => ({
                            label: tab.label,
                            action: () => setSelectedSecondaryIndex(i),
                            count: tab.count,
                        }))}
                        selectedIndex={selectedSecondaryIndex}
                    />
                </div>
            )}

            

            {datasetMutation.isLoading || protocolMutation.isLoading || transformedData.length > 0 || originalData.length > 0 ? (
                <>
                    <div className="w-full flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={isOriginalShown ? originalHeaders : transformedHeaders} 
                            setHeaders={isOriginalShown ? setOriginalHeaders : setTransformedHeaders} 
                            isDisabled={false}
                        />
                        <CheckboxButton 
                            action={handleCheck}
                            isChecked={isOriginalShown}
                            label='Show Original Fields'
                            id='show_original_fields'
                        />
                    </div>
                    <ProtocolTable 
                        isLoading={datasetMutation.isLoading || protocolMutation.isLoading} 
                        headers={isOriginalShown ? originalHeaders : transformedHeaders}
                        tableData={isOriginalShown ? originalData : transformedData}
                        protocol={protocol}
                        selectedStage={secondaryTabs.length > 0 ? secondaryTabs[selectedSecondaryIndex].label : ''}
                    />
                </>
            ) : (
                <div className="w-full p-10 flex flex-col justify-center items-center gap-8">
                    <img src={`${process.env.PUBLIC_URL}/no-records/${palette.logo}`} className="w-[160px]" alt='no-records'/>
                    <h3 className="text-[16px] font-medium">No Records</h3>
                </div>
            )}

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>
            
        </div>
    )
}